import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

// NOTE: The admin/doctor/patient dashboards moved to real-time Socket.IO
// (see hooks/useDashboardSocket.js + backend socket/dashboardSocket.js), so all of
// their REST thunks were removed from this slice. The two thunks below are the
// only ones still referenced (by components/dashboard/AppointmentTabs.jsx, which is
// itself currently unused) and hit endpoints shared with the mobile app — they are
// kept until AppointmentTabs is removed or migrated.

// Build the optional dashboard filter query-string fragment.
// Returns e.g. "&specialtyId=SPEC1&addressTypeName=Clinic" or "" when no filters set.
const filterQS = ({ specialtyId, addressTypeName } = {}) =>
    `${specialtyId ? `&specialtyId=${encodeURIComponent(specialtyId)}` : ''}` +
    `${addressTypeName ? `&addressTypeName=${encodeURIComponent(addressTypeName)}` : ''}`;

// ✅ Get Doctor Upcoming Appointments by Specialty
export const doctorUpcomingAppointments = createAsyncThunk(
    'dashboard/doctorUpcomingAppointments',
    async ({ docId, startDate, endDate, specialtyId, addressTypeName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/dashboard/UpcomingAppt?docId=${docId}&startDate=${startDate}&endDate=${endDate}${filterQS({ specialtyId, addressTypeName })}`
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch upcoming appointments';
            return rejectWithValue({ error: errorMessage });
        }
    }
);

// ✅ Get Doctor Appointments by Specialty
export const doctorAppointmentsSpecialty = createAsyncThunk(
    'dashboard/doctorAppointmentsSpecialty',
    async ({ specialtyId, docId, startDate, endDate, page = 1, limit = 4, addressTypeName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/dashboard/apptBySpecialty?specialtyId=${specialtyId}&docId=${docId}&startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}${filterQS({ addressTypeName })}`
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch appointments by specialty';
            return rejectWithValue({ error: errorMessage });
        }
    }
);

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState: {
        upcomingAppointments: null,
        appointmentsBySpecialty: null,
        upcomingLoading: false,
        specialtyLoading: false,
        error: null,
    },
    reducers: {
        clearDashboardError: (state) => {
            state.error = null;
        },
        clearUpcomingAppointments: (state) => {
            state.upcomingAppointments = null;
        },
        clearAppointmentsBySpecialty: (state) => {
            state.appointmentsBySpecialty = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Doctor Upcoming Appointments
            .addCase(doctorUpcomingAppointments.pending, (state) => {
                state.upcomingLoading = true;
                state.error = null;
            })
            .addCase(doctorUpcomingAppointments.fulfilled, (state, action) => {
                state.upcomingLoading = false;
                state.upcomingAppointments = action.payload;
            })
            .addCase(doctorUpcomingAppointments.rejected, (state, action) => {
                state.upcomingLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch upcoming appointments');
            })
            // Doctor Appointments by Specialty
            .addCase(doctorAppointmentsSpecialty.pending, (state) => {
                state.specialtyLoading = true;
                state.error = null;
            })
            .addCase(doctorAppointmentsSpecialty.fulfilled, (state, action) => {
                state.specialtyLoading = false;
                state.appointmentsBySpecialty = action.payload;
            })
            .addCase(doctorAppointmentsSpecialty.rejected, (state, action) => {
                state.specialtyLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch appointments by specialty');
            });
    },
});

export const { clearDashboardError, clearUpcomingAppointments, clearAppointmentsBySpecialty } = dashboardSlice.actions;
export default dashboardSlice.reducer;
